import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Sparkles } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useUserStore } from '@/store/userStore';

/**
 * «Что ты уже собрал» — на экране оплаты, НАД ценой.
 *
 * Зачем (решение Славы 04.09.2026): раньше человек упирался в прайс
 * с абстрактным списком возможностей и решал «нужно ли мне приложение
 * за 990». Со сводкой он решает другое — «выбросить ли то, что я уже
 * собрал». Второе продаёт само, потому что это его собственные данные,
 * а не наше обещание.
 *
 * Пробный период три дня, и стена встаёт ровно на достижении «Три дня
 * подряд» — в момент подъёма. Здесь этот подъём и показывается цифрами.
 */

type Summary = {
  days: number;
  meals: number;
  photos: number;
  active_days: number;
  avg_calories: number;
  avg_protein: number;
  goal_protein: number;
  protein_low_days: number;
  veg_ok: boolean;
  weight_delta: number | null;
};

/** Одна фраза Славы про то, что он уже знает. Не список, а наблюдение. */
function slavaKnows(s: Summary): string | null {
  if (s.goal_protein > 0 && s.protein_low_days >= 2) {
    return `Белка ты недобираешь — в среднем ${s.avg_protein} г при норме ${s.goal_protein}. Я это запомнил и буду напоминать.`;
  }
  if (s.weight_delta !== null && s.weight_delta < 0) {
    return `Вес уже ушёл на ${Math.abs(s.weight_delta).toFixed(1)} кг. Такое начало стоит продолжить.`;
  }
  if (s.veg_ok) {
    return 'С овощами у тебя всё в порядке — это редкость, и это твоя сильная сторона.';
  }
  if (s.avg_calories > 0) {
    return `В среднем выходит ${s.avg_calories} ккал в день. Дальше я буду точнее — чем дольше пишешь, тем лучше я тебя знаю.`;
  }
  return null;
}

export function MySummaryCard() {
  const { profile } = useUserStore();
  const [s, setS] = useState<Summary | null>(null);

  useEffect(() => {
    if (!profile?.id) return;
    supabase.rpc('get_my_summary').then(({ data }) => {
      if (data && typeof data === 'object' && (data as any).meals !== undefined) setS(data as Summary);
    });
  }, [profile?.id]);

  // Тому, кто ничего не записал, показывать нечего — и врать «ты собрал» нельзя.
  if (!s || s.meals === 0) return null;

  const knows = slavaKnows(s);
  const dayWord = (n: number) => (n % 10 === 1 && n % 100 !== 11 ? 'день' : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20) ? 'дня' : 'дней');

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="card p-4 mb-5"
      style={{ borderColor: 'rgba(16,229,142,0.28)' }}
    >
      <div className="text-[11px] text-emerald-glow uppercase tracking-wider font-medium mb-3">
        Что ты уже собрал
      </div>

      <div className="grid grid-cols-3 gap-2 text-center mb-3">
        <div>
          <div className="font-display text-2xl font-bold text-emerald-glow">{s.meals}</div>
          <div className="text-[10px] text-ink-dim">записей о еде</div>
        </div>
        <div>
          <div className="font-display text-2xl font-bold">{s.active_days}</div>
          <div className="text-[10px] text-ink-dim">{dayWord(s.active_days)} с дневником</div>
        </div>
        <div>
          <div className="font-display text-2xl font-bold">{s.photos}</div>
          <div className="text-[10px] text-ink-dim">блюд по фото</div>
        </div>
      </div>

      {knows && (
        <div className="flex items-start gap-2.5 pt-3 border-t border-line">
          <div className="w-[26px] h-[26px] rounded-full bg-emerald-glow/15 flex items-center justify-center shrink-0 mt-0.5">
            <Sparkles size={14} className="text-emerald-glow" />
          </div>
          <div className="flex-1 text-[13px] leading-snug text-ink">
            {knows}
            <div className="text-[11px] text-ink-dim mt-1">— Дофи</div>
          </div>
        </div>
      )}

      <div className="text-[11px] text-ink-dim mt-3">
        Всё это останется с тобой — история никуда не денется.
      </div>
    </motion.div>
  );
}
